// the root component combines reducers, sets up the store and ties routing components together.
/* eslint-disable jsdoc/no-undefined-types */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { Redirect } from 'react-router';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import asyncComponent from 'neon-frontend-utils/src/components/asyncComponent';
import ApiFactory from 'neon-frontend-utils/src/api/factory';
import NeonAuthenticator from 'neon-frontend-utils/src/authenticator/neon';
import NeonAuthoriser from 'neon-frontend-utils/src/authoriser/neon';
import Logger from 'neon-frontend-utils/src/logger';
import Components from './constants/Components';
import AppConfig from './App.config';
import Alert from 'neon-frontend-utils/src/components/Alert';
import * as Sentry from '@sentry/react';
import MaintenanceNotificationBar from 'neon-frontend-utils/src/components/MaintenanceNotificationBar/MaintenanceNotificationBar';

if (AppConfig.sentry.environments.includes(AppConfig.targetEnv)) {
    const dsn = AppConfig.targetEnv === 'development' ? AppConfig.sentry.developDsn : AppConfig.sentry.dsn;

    Sentry.init({
        dsn,
        environment: AppConfig.targetEnv,
        integrations: [
            new Sentry.BrowserTracing(),
            new Sentry.Replay({
                blockAllMedia: false
            })
        ],
        release: AppConfig.sentry.release,

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: AppConfig.sentry.sampleRate,
        replaysSessionSampleRate: AppConfig.sentry.replayRate,
        replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
}

// init logger
const logger = new Logger(AppConfig.logger);

logger.notice({
    component: Components.APPLICATION,
    message: 'Open application'
});

// import icon definitions (this is now done from external file so it can be imported by Jest)
import { faIcons } from './faIcons'; // eslint-disable-line no-unused-vars

// react-redux: make the store available to all container components in the application without passing it explicitly
import { Provider } from 'react-redux';

// this defines the redux store
import { createStore, combineReducers } from 'redux';

// import all reducers
import participationsReducer from './pages/Participations/reducers/participations';
import languageReducer from './components/SetLanguage/reducers/language';
import formReducer from 'neon-frontend-utils/src/components/Form/reducers/form';
import featureToggleReducer from './pages/FeatureToggles/reducers/featuretoggles';
import alertReducer from 'neon-frontend-utils/src/components/Alert/reducers/alert';
import reportsReducer from './pages/Reports/reducers/reports';
import reportReducer from './pages/Report/reducers/report';

// combine into one
const rootReducer = combineReducers({
    participationsReducer,
    languageReducer,
    formReducer,
    featureToggleReducer,
    alertReducer,
    reportsReducer,
    reportReducer
});

// configure redux store with the combined reducers
const store = createStore(rootReducer);

// keep browser localStorage in sync with the featureToggles state
store.subscribe(() => {
    if (localStorage.getItem('featureToggles') !== JSON.stringify(store.getState().featureToggleReducer.featureToggles)) {
        localStorage.setItem('featureToggles', JSON.stringify(store.getState().featureToggleReducer.featureToggles));
    }

    if (localStorage.getItem('languageId') !== JSON.stringify(store.getState().languageReducer.languageId)) {
        localStorage.setItem('languageId', JSON.stringify(store.getState().languageReducer.languageId));
    }
});

// configure the Neon API once, so we can use it in any component from now
// this can be fetched by calling ApiFactory.get('neon')
ApiFactory.create(
    'neon',
    AppConfig.api.neon,
    new NeonAuthenticator(AppConfig.authenticator.neon, AppConfig.authenticator.cognito),
    new NeonAuthoriser(AppConfig.authoriser.neon)
);

const api = ApiFactory.get('neon');

// configure API for ApiPlatform
ApiFactory.create(
    'neon-v2', // name
    AppConfig.api.neon, // apiConfig, for now its merged with v1 because most props overlap
    new NeonAuthenticator(AppConfig.authenticator.neon, AppConfig.authenticator.cognito), // reusing authenticator
    new NeonAuthoriser(AppConfig.authoriser.neon), // reusing authoriser
    'apiPlatform'
);
const apiPlatform = ApiFactory.get('neon-v2'); // instance name

// These components are dependant on the neon api instance
import AuthorisedRoute from 'neon-frontend-utils/src/components/AuthorisedRoute';

// import Authenticated from 'neon-frontend-utils/src/components/Authenticated';

// import common css so it becomes available in all page components and eases separation for client specific css.
import style from '../style/global.scss'; // eslint-disable-line no-unused-vars

/**
 * Returns the PWA page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} questionnaire page
 */
const getPwa = asyncComponent(() => import('./pages/Pwa'));

/**
 * Returns the Login page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} login page
 */
const getLogin = asyncComponent(() => import('./pages/Login'));

/**
 * Returns the Error page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} error page
 */
const getError = asyncComponent(() => import('./pages/Error'));

/**
 * Returns the LTPImpact page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} error page
 */
const getLtpImpact = asyncComponent(() => import('./pages/LtpImpact'));

/**
 * Returns the Forgot Password page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} forgot password page
 */
const getForgotPassword = asyncComponent(() => import('./pages/ForgotPassword'));

/**
 * Returns the Participations page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} participations page
 */
const getParticipations = asyncComponent(() => import('./pages/Participations'));

/**
 * Returns the register page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} register page
 */
const getRegister = asyncComponent(() => import('./pages/Register'));

/**
 * Returns the page to amend participant details
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} amend details page
 */
const getAmendDetails = asyncComponent(() => import('./pages/AmendDetails'));

/**
 * Returns the page to practice site
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} practice site page
 */
const getPracticeSite = asyncComponent(() => import('./pages/PracticeSite'));

/**
 * Returns the assessmentReport page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} assessmentReport page
 */
const getAssessmentReport = asyncComponent(() => import('./pages/Report'));

/**
 * Returns the getProfile page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} getProfile page
 */
const getProfile = asyncComponent(() => import('./pages/Profile'));

/**
 * Returns the featureToggles page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} featureToggles page
 */
const getFeatureToggles = asyncComponent(() => import('./pages/FeatureToggles'));

/**
 * Renders the reports
 * @returns {{}} reports
 */
const getReports = asyncComponent(() => import('./pages/Reports'));

/**
 * Returns maintenance page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} maintenance page
 */
const getMaintenance = asyncComponent(() => import('./pages/Maintenance'));

/**
 * Returns the QuestionnairesV2 page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} questionnairesV2 page
 */
const getQuestionnairesV2 = asyncComponent(() => import('./pages/QuestionnairesV2'));

/**
 * Returns the QuestionnaireV2 page
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} questionnaireV2 page
 */
const getQuestionnaireV2 = asyncComponent(() => import('./pages/QuestionnaireV2'));

/**
 * Returns the SelfRegistration page
 * TODO: Check @returns
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>} selfRegistration page
 */
const getSelfRegistration = asyncComponent(() => import('./pages/SelfRegistration'));


/**
 * Returns the MagicLink page
 * TODO: Check @returns
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>}
 */
const getMagicLink = asyncComponent(() => import('./pages/MagicLink'));

/**
 * Returns the Group Report page
 * TODO: Check @returns
 * @returns {any | Promise | * | PromiseLike<T> | Promise<T>}
 */
const getGroupReport = asyncComponent(() => import('./pages/GroupReportPage'));


/**
 * Renders the app
 * @returns {{}} app
 */

class App extends Component {
    render() {
        const { maintenance, maintenanceDate } = this.props;

        if (maintenance) {
            return (
                <Provider store={ store }>
                    <section id="layout">
                        <BrowserRouter>
                            <div>
                                <Switch>
                                    <Route component={ getMaintenance } />
                                </Switch>
                            </div>
                        </BrowserRouter>
                    </section>
                </Provider>
            );
        }

        const maintenanceNotification = maintenanceDate
            ? <MaintenanceNotificationBar
                maintenanceDate={maintenanceDate}
                languageId={store?.getState()?.languageReducer?.languageId || AppConfig.languages.defaultLanguage}
            />
            : null;

        return (
            <Provider store={ store }>
                <section id="layout">
                    <BrowserRouter>
                        <div>
                            { maintenanceNotification }
                            <Alert />
                            <Switch>
                                <Route exact path="/portal/maintenance" component={ getMaintenance } />
                                <Route exact path="/portal" render={() => <Redirect to="/portal/participations"/>}/>
                                <Route path="/portal/login" component={ getLogin } />
                                <Route path="/portal/ltpimpact" component={ getLtpImpact } />

                                {/* these two are old practice site routes that can be left in since they are reused */}
                                <Route exact path="/portal/practice" component={ getPracticeSite } />
                                <Route exact path="/portal/practice/level/:educationLevelName" component={ getPracticeSite } />

                                {/* assessmentv2 route to start a practice questionnaire */}
                                <Route path="/portal/questionnaire-practice/:practiceQuestionnaireId/:start/:practiceQuestionnaireHandle?/:practiceQuestionnaireLevel?/:isExplanation?" component={ getQuestionnaireV2 } componentName="questionnaireV2" apiPlatform={apiPlatform} />

                                <Route path="/portal/forgot-password" component={ getForgotPassword } />
                                <Route path="/portal/register/:accountHasRoleSlug?" component={ getRegister } />

                                { /* requires params: email and token */ }
                                <Route path="/portal/reset-password" component={ getForgotPassword }/>

                                <AuthorisedRoute exact api={api} path="/portal/participations" component={ getParticipations } componentName="participations" />
                                <AuthorisedRoute api={api} path="/portal/assessmentreport/:participantSessionSlug/:reportSectionHandle" component={ getAssessmentReport } componentName="assessmentReport" />
                                <AuthorisedRoute api={api} path="/portal/assessmentreport/:participantSessionSlug" component={ getAssessmentReport } componentName="assessmentReport" />
                                <AuthorisedRoute api={api} path="/portal/amend_details/:participantSessionId" component={ getAmendDetails } componentName="amendDetails" />
                                <AuthorisedRoute api={api} path="/portal/participations/:participantSessionSlug" component={ getParticipations } componentName="participations" />

                                <AuthorisedRoute api={api} path="/portal/questionnaires/:participantSessionId" component={ getQuestionnairesV2 } componentName="questionnairesV2" apiPlatform={apiPlatform}/>

                                <AuthorisedRoute api={api} path="/portal/questionnaire-session/:questionnaireSessionId" component={ getQuestionnaireV2 } componentName="questionnaireV2" apiPlatform={apiPlatform} />

                                <AuthorisedRoute api={api} path="/portal/questionnaire-session-intro/:pSessionId/:questionnaireGroupId" component={ getQuestionnaireV2 } componentName="questionnaireV2" apiPlatform={apiPlatform} />

                                <AuthorisedRoute api={api} path="/portal/profile" component={ getProfile } componentName="profile" />

                                <AuthorisedRoute api={api} path="/portal/pwa" component={ getPwa } componentName="pwa" />

                                <AuthorisedRoute api={api} path="/portal/featureToggles" component={ getFeatureToggles } componentName="featureToggles" />
                                <AuthorisedRoute api={api} path="/portal/reports" component={ getReports } componentName="reports"/>
                                <AuthorisedRoute api={api} path="/portal/group-report/:id" component={ getGroupReport } componentName="groupReportPage"/>

                                <Route path="/portal/magic-link/:linkHash" component={ getMagicLink } componentName="getMagicLink" />
                                <Route path="/portal/self-registration/:assessmentTemplateUuid/:assessmentTemplateId" component={ getSelfRegistration } componentName="getSelfRegistration" />

                                <Route path="/portal/error" component={ getError } />
                                <Route component={ getError } />
                            </Switch>
                        </div>
                    </BrowserRouter>
                </section>
            </Provider>
        );
    }
}

// before rendering the app, always first fetch the current user (if available)
api.getAuthenticator().refreshAndGetUser().then(user => {
    let maintenance = false;

    if (user.maintenance === true) {
        maintenance = true;
    }

    const getMaintenanceDate = () => {
        const maintenanceDate = api.getMaintenanceDate();

        if (maintenanceDate && new Date(maintenanceDate) >= new Date()) {
            return maintenanceDate;
        }

        return null;
    };

    const maintenanceDate = getMaintenanceDate();

    ReactDOM.render(<App user={user} maintenance={ maintenance } maintenanceDate={maintenanceDate} />,
        document.getElementById('root'));

}).catch(() => {
    ReactDOM.render(<App />,
        document.getElementById('root'));
});
